import AbcIcon from '@mui/icons-material/Abc';
import BadgeIcon from '@mui/icons-material/Badge';
import CampaignIcon from '@mui/icons-material/Campaign';
import CategoryIcon from '@mui/icons-material/Category';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EmailIcon from '@mui/icons-material/Email';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import GroupIcon from '@mui/icons-material/Group';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import MergeIcon from '@mui/icons-material/Merge';
import MonitorIcon from '@mui/icons-material/Monitor';
import PaidIcon from '@mui/icons-material/Paid';
import PaymentIcon from '@mui/icons-material/Payment';
import PermDataSettingIcon from '@mui/icons-material/PermDataSetting';
import RateReviewIcon from '@mui/icons-material/RateReview';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import TextIncreaseIcon from '@mui/icons-material/TextIncrease';
import UnsubscribeIcon from '@mui/icons-material/Unsubscribe';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import WorkIcon from '@mui/icons-material/Work';

import type { SidebarEntry, SidebarLinkEntry, SidebarMenuItem, SidebarSectionEntry } from './types';

function createItem(
  id: string,
  label: string,
  to: string,
  activePatterns: string[] = [to],
): SidebarMenuItem {
  return {
    id,
    label,
    to,
    activePatterns,
  };
}

function createLink(
  id: string,
  label: string,
  to: string,
  icon: SidebarLinkEntry['icon'],
  activePatterns: string[] = [to],
): SidebarLinkEntry {
  return {
    type: 'link',
    id,
    label,
    to,
    icon,
    activePatterns,
  };
}

function createSection(
  id: string,
  label: string,
  icon: SidebarSectionEntry['icon'],
  items: SidebarMenuItem[],
): SidebarSectionEntry {
  return {
    type: 'section',
    id,
    label,
    icon,
    items,
  };
}

const dashboardEntry = createLink(
  'dashboard',
  'Dashboard',
  '/admin/admins/dashboard',
  DashboardIcon,
);

const limitedConfigurationItems = [
  createItem('change-username', 'Change Username', '/admin/admins/changeusername'),
  createItem('change-password', 'Change Password', '/admin/admins/changepassword'),
  createItem('change-email', 'Change Email', '/admin/admins/changeemail'),
];

const superConfigurationItems = [
  ...limitedConfigurationItems,
  createItem('security-questions', 'Security Questions', '/admin/admins/securityQuestions'),
  createItem('manage-plans', 'Manage Plans', '/admin/manage-plans', [
    '/admin/manage-plans',
    '/admin/plans/addplan',
    '/admin/plan/*/edit',
  ]),
  createItem('contact-address', 'Set Contact Us Address', '/admin/admins/settings'),
  createItem('change-slogan', 'Slogan Text', '/admin/admins/changeSlogan'),
  createItem('change-logo', 'Change Logo', '/admin/admins/uploadLogo'),
  createItem('change-favicon', 'Change Favicon', '/admin/admins/changeFavicon'),
  createItem('change-color-theme', 'Change Color Theme', '/admin/admins/changecolorscheme'),
  createItem('meta-management', 'Meta Management', '/admin/admins/metaManagement'),
  createItem('manage-sub-admins', 'Manage Sub Admins', '/admin/admins/manage', [
    '/admin/admins/manage',
    '/admin/admins/addsubadmin',
    '/admin/admins/editadmins/*',
    '/admin/admins/managerole/*',
  ]),
  createItem('smtp-setting', 'SMTP Setting', '/admin/smtpsettings/configuration'),
];

const settingsItems = [
  createItem('site-setting', 'Site Setting', '/admin/settings/siteSettings'),
  createItem('manage-email-setting', 'Manage Email Setting', '/admin/settings/manageMails', [
    '/admin/settings/manageMails',
    '/admin/settings/editMails/*',
  ]),
];

const employersItems = [
  createItem('employer-list', 'Employer List', '/admin/users', [
    '/admin/users',
    '/admin/users/editusers/*',
  ]),
  createItem('activity-list', 'Activity List', '/admin/users/activity'),
  createItem('add-employer', 'Add Employer', '/admin/users/addusers'),
  createItem('home-page-slider', 'Home Page Slider', '/admin/users/selectforslider'),
];

const paymentHistoryItems = [
  createItem('transaction-list', 'Transaction List', '/admin/payments/history'),
];

const jobsItems = [
  createItem('jobs-list', 'Jobs List', '/admin/jobs', [
    '/admin/jobs',
    '/admin/jobs/editjob/*',
    '/admin/jobs/candidates/*',
    '/admin/jobs/addjob/*',
  ]),
  createItem('add-job', 'Add Job', '/admin/jobs/addjob'),
  createItem('import-job', 'Import Job', '/admin/jobs/import'),
  createItem('auto-job-import-list', 'Auto Job Import List', '/admin/jobs/importlist'),
];

const jobseekersItems = [
  createItem('jobseekers-list', 'Jobseekers List', '/admin/candidates', [
    '/admin/candidates',
    '/admin/candidates/editcandidates/*',
    '/admin/candidates/certificates/*',
    '/admin/jobs/applied/*',
  ]),
  createItem('add-jobseekers', 'Add Jobseekers', '/admin/candidates/addcandidates'),
];

const resumesItems = [
  createItem('resumes-list', 'Resumes List', '/admin/resumes', [
    '/admin/resumes',
    '/admin/resumes/*',
  ]),
  createItem('simple-resumes', 'Simple Resumes', '/admin/simple-resumes', [
    '/admin/simple-resumes',
    '/admin/simple-resumes/*',
  ]),
];

const seoItems = [
  createItem('seo-list', 'SEO List', '/admin/seo-list'),
  createItem('add-seo', 'Add New SEO Info', '/admin/seo-list/add-new-seo-info', [
    '/admin/seo-list/add-new-seo-info',
    '/admin/seo-list/*',
  ]),
];

const redirectsItems = [
  createItem('redirects-list', 'Redirects List', '/admin/redirects'),
  createItem('add-redirect', 'Add New Redirect', '/admin/redirects/add-new-redirect', [
    '/admin/redirects/add-new-redirect',
    '/admin/redirects/*',
  ]),
];

const categoriesItems = [
  createItem('categories-list', 'Categories List', '/admin/categories', [
    '/admin/categories',
    '/admin/categories/editcategory/*',
    '/admin/categories/subindex/*',
    '/admin/categories/editsubcat/*',
  ]),
  createItem('add-category', 'Add Category', '/admin/categories/addcategory'),
];

const swearWordsItems = [
  createItem('swear-words-list', 'Swear Words List', '/admin/swears', [
    '/admin/swears',
    '/admin/swears/editswear/*',
  ]),
  createItem('add-swear-words', 'Add Swear Words', '/admin/swears/addswears'),
];

const skillsItems = [
  createItem('skills-list', 'Skills List', '/admin/skills', [
    '/admin/skills',
    '/admin/skills/editskill/*',
  ]),
  createItem('add-skills', 'Add Skills', '/admin/skills/addskills'),
];

const designationsItems = [
  createItem('designations-list', 'Designations List', '/admin/designations', [
    '/admin/designations',
    '/admin/designations/editdesignation/*',
  ]),
  createItem('add-designations', 'Add Designations', '/admin/designations/adddesignations'),
];

const currencyItems = [
  createItem('currency-list', 'Currency List', '/admin/currencies', [
    '/admin/currencies',
    '/admin/currencies/edit/*',
  ]),
  createItem('add-currency', 'Add Currency', '/admin/currencies/add'),
];

const newsletterItems = [
  createItem('newsletter-list', 'Newsletter List', '/admin/newsletters/index', [
    '/admin/newsletters/index',
    '/admin/newsletters/addNewsletter',
    '/admin/newsletters/editNewsletter/*',
  ]),
  createItem(
    'send-newsletter',
    'Send Newsletter Email',
    '/admin/newsletters/sendNewsletter',
  ),
  createItem('email-logs', 'Email Logs', '/admin/newsletters/sentMail'),
  createItem(
    'unsubscribe-users',
    'Unsubscribe User List',
    '/admin/newsletters/unsubscriberlist',
  ),
];

const bannerItems = [
  createItem('banner-list', 'Banner List', '/admin/banneradvertisements', [
    '/admin/banneradvertisements',
    '/admin/banneradvertisements/editBanneradvertisement/*',
  ]),
  createItem(
    'add-banner',
    'Add Banner',
    '/admin/banneradvertisements/addBanneradvertisement',
  ),
];

const courseItems = [
  createItem('course-list', 'Course List', '/admin/courses', [
    '/admin/courses',
    '/admin/courses/editcourse/*',
    '/admin/specializations/index/*',
    '/admin/specializations/addspecialization/*',
    '/admin/specializations/editspecialization/*',
  ]),
  createItem('add-course', 'Add Course', '/admin/courses/addcourse'),
];

const contentsItems = [
  createItem('pages-list', 'Pages List', '/admin/pages/index', [
    '/admin/pages/index',
    '/admin/pages/editPage/*',
  ]),
];

const emailTemplateItems = [
  createItem('email-template-setting', 'Email Template Setting', '/admin/emailtemplates', [
    '/admin/emailtemplates',
    '/admin/emailtemplates/editEmailtemplate/*',
  ]),
];

const blogsItems = [
  createItem('blog-list', 'Blog List', '/admin/blogs', [
    '/admin/blogs',
    '/admin/blogs/editblogs/*',
  ]),
  createItem('add-blog', 'Add Blog', '/admin/blogs/addblogs'),
];

const slidersItems = [
  createItem('slider-list', 'Slider List', '/admin/sliders', [
    '/admin/sliders',
    '/admin/sliders/edit/*',
  ]),
  createItem('add-slider', 'Add Slider', '/admin/sliders/add'),
];

const announcementsItems = [
  createItem('announcement-list', 'Announcement List', '/admin/announcements', [
    '/admin/announcements',
    '/admin/announcements/edit/*',
  ]),
  createItem('add-announcement', 'Add Announcement', '/admin/announcements/add'),
];

const keywordsItems = [
  createItem('search-keywords', 'Search Keywords', '/admin/keywords', [
    '/admin/keywords',
    '/admin/keywords/add',
    '/admin/keywords/edit/*',
  ]),
  createItem('job-keywords', 'Job Keywords', '/admin/keywords/jobs', [
    '/admin/keywords/jobs',
    '/admin/keywords/addjobs',
    '/admin/keywords/editjobs/*',
  ]),
  createItem('requested-keywords', 'Requested Keywords', '/admin/keywords/requests'),
];

export const LIMITED_ADMIN_ENTRIES: SidebarEntry[] = [
  dashboardEntry,
  createSection('configuration', 'Configurations', PermDataSettingIcon, limitedConfigurationItems),
  createSection('employers', 'Employers', BadgeIcon, employersItems),
  createSection('payment-history', 'Payment History', PaymentIcon, paymentHistoryItems),
  createSection('jobs', 'Jobs', WorkIcon, jobsItems),
  createSection('jobseekers', 'Jobseekers', GroupIcon, jobseekersItems),
  createSection('resumes', 'Resumes', TextIncreaseIcon, resumesItems),
  createSection('currency', 'Currency', PaidIcon, currencyItems),
  createSection('contents', 'Contents', MonitorIcon, contentsItems),
  createSection('blogs', 'Blogs', RateReviewIcon, blogsItems),
  createSection('sliders', 'Sliders', ViewCarouselIcon, slidersItems),
  createSection('announcements', 'Announcement', CampaignIcon, announcementsItems),
  createSection('keywords', 'Keywords', FindInPageIcon, keywordsItems),
];

export const SUPER_ADMIN_ENTRIES: SidebarEntry[] = [
  dashboardEntry,
  createSection('configuration', 'Configurations', PermDataSettingIcon, superConfigurationItems),
  createSection('settings', 'Settings', SettingsApplicationsIcon, settingsItems),
  createSection('employers', 'Employers', BadgeIcon, employersItems),
  createSection('payment-history', 'Payment History', PaymentIcon, paymentHistoryItems),
  createSection('jobs', 'Jobs', WorkIcon, jobsItems),
  createSection('jobseekers', 'Jobseekers', GroupIcon, jobseekersItems),
  createSection('resumes', 'Resumes', TextIncreaseIcon, resumesItems),
  createSection('seo', 'SEO', FindInPageIcon, seoItems),
  createSection('redirects', 'Redirects', MergeIcon, redirectsItems),
  createSection('categories', 'Categories', CategoryIcon, categoriesItems),
  createSection('swear-words', 'Swear Words', AbcIcon, swearWordsItems),
  createSection('skills', 'Skills', TextIncreaseIcon, skillsItems),
  createSection('designations', 'Designations', LibraryBooksIcon, designationsItems),
  createSection('currency', 'Currency', PaidIcon, currencyItems),
  createSection(
    'manage-newsletter',
    'Manage Newsletter',
    UnsubscribeIcon,
    newsletterItems,
  ),
  createSection(
    'banner-advertisement',
    'Banner Advertisement',
    ViewCarouselIcon,
    bannerItems,
  ),
  createSection('course', 'Course', LibraryBooksIcon, courseItems),
  createSection('contents', 'Contents', MonitorIcon, contentsItems),
  createSection('email-templates', 'Email Templates', EmailIcon, emailTemplateItems),
  createSection('blogs', 'Blogs', RateReviewIcon, blogsItems),
  createSection('sliders', 'Sliders', ViewCarouselIcon, slidersItems),
  createSection('announcements', 'Announcement', CampaignIcon, announcementsItems),
  createSection('keywords', 'Keywords', FindInPageIcon, keywordsItems),
];

export function getSidebarEntries(isSuperAdmin: boolean) {
  return isSuperAdmin ? SUPER_ADMIN_ENTRIES : LIMITED_ADMIN_ENTRIES;
}
